import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreateCustomerRequest, Customer, UpdateCustomerRequest } from '../types';
import { apiClient } from '../services/api';
import { restorePaginatedQueries, snapshotPaginatedQueries, updatePaginatedQueries, PaginatedSnapshot } from '../utils/queryCache';

type SaveCustomerInput = {
  id?: string;
  data: CreateCustomerRequest | UpdateCustomerRequest;
};

export const useCustomerMutations = () => {
  const queryClient = useQueryClient();

  const invalidateCustomers = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['customers'] }),
      queryClient.invalidateQueries({ queryKey: ['admin'] }),
    ]);
  };

  const saveCustomerMutation = useMutation({
    mutationFn: async ({ id, data }: SaveCustomerInput) => {
      if (id) return apiClient.updateCustomer(id, data as UpdateCustomerRequest);
      return apiClient.createCustomer(data as CreateCustomerRequest);
    },
    onSuccess: invalidateCustomers,
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteCustomer(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ['customers'] });
      const snapshots = snapshotPaginatedQueries<Customer>(queryClient, 'customers');
      updatePaginatedQueries<Customer>(queryClient, 'customers', page => ({
        ...page,
        content: page.content.filter(customer => customer.id !== id),
        totalElements: Math.max(0, page.totalElements - 1),
      }));
      return { snapshots };
    },
    onError: (_error, _id, context: { snapshots: PaginatedSnapshot<Customer> } | undefined) => {
      if (context) restorePaginatedQueries(queryClient, context.snapshots);
    },
    onSettled: invalidateCustomers,
  });

  return {
    saveCustomerMutation,
    deleteCustomerMutation,
  };
};
